/**
 * Distribution Chart
 *
 * Compact bar chart of labeled, colored bins — used by the View surface for the
 * slope-angle distribution. Vertical by default (bar height ∝ the largest bin);
 * `horizontal` lays the bins out as ordered rows with full-width left labels.
 * Each bar's title shows its share of the total.
 */

import { LitElement, html, css, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import type { DistributionBin } from '../../lib/geometry-view.js'

@customElement('distribution-chart')
export class DistributionChart extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .heading {
      font-size: 11px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.85);
      margin-bottom: 6px;
    }

    .plot {
      display: flex;
      align-items: flex-start;
      gap: 4px;
    }

    /* Percentage y-axis: 0 at the bottom, the largest bin's share at the top */
    .yaxis {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: 24px;
      height: 118px;
      flex-shrink: 0;
      font-size: 10px;
      text-align: right;
      color: rgba(255, 255, 255, 0.85);
    }

    .bars {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: flex-end;
      gap: 4px;
    }

    .col {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .track {
      width: 100%;
      height: 118px;
      display: flex;
      align-items: flex-end;
      justify-content: center;
    }

    .bar {
      width: 100%;
      max-width: 26px;
      border-radius: 2px 2px 0 0;
      min-height: 1px;
    }

    .label {
      font-size: 11px;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.9);
      margin-top: 3px;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: center;
    }

    .empty {
      font-size: 11px;
      opacity: 0.5;
    }

    /* Sparse axis (continuous charts): start / mid / end spread across the width.
     *  Offset to line up under the bars (past the y-axis gutter). */
    .axis {
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.9);
      margin: 4px 0 0 28px;
    }

    /* Horizontal layout: ordered rows with the label at full width on the left
     *  (no ellipsis), the bar growing rightward, and the percent at the right. */
    .hrows {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .hrow {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .hlabel {
      width: 48px;
      flex-shrink: 0;
      font-size: 11px;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.9);
      text-align: right;
      white-space: nowrap;
    }

    .htrack {
      flex: 1;
      min-width: 0;
      height: 12px;
      background: rgba(255, 255, 255, 0.08);
      border-radius: 2px;
      overflow: hidden;
    }

    .hbar {
      display: block;
      height: 100%;
      border-radius: 2px;
      min-width: 1px;
    }

    .hpct {
      width: 30px;
      flex-shrink: 0;
      font-size: 10px;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.85);
      text-align: right;
    }
  `

  @property({ type: String }) heading = ''
  @property({ attribute: false }) bins: DistributionBin[] = []
  /** For continuous distributions: [start, mid, end] labels spread along the
   *  axis instead of one (truncated) label per bar. */
  @property({ attribute: false }) axisLabels?: string[]
  /** Lay bars out as ordered horizontal rows (label left, bar right) — better
   *  for ordered bands whose labels don't fit under a narrow vertical bar. */
  @property({ type: Boolean }) horizontal = false

  override render(): unknown {
    const total = this.bins.reduce((s, b) => s + b.value, 0)
    const max = Math.max(1, ...this.bins.map(b => b.value))
    if (total === 0) {
      return html`<div class="heading">${this.heading}</div>
        <div class="empty">No data</div>`
    }
    if (this.horizontal) {
      return html`
        <div class="heading">${this.heading}</div>
        <div class="hrows">
          ${this.bins.map(b => {
            const pct = Math.round((b.value / total) * 100)
            const title = b.hint ? `${pct}% — ${b.hint}` : `${b.label}: ${pct}%`
            return html`<div class="hrow" title=${title}>
              <span class="hlabel">${b.label}</span>
              <span class="htrack">
                <span
                  class="hbar"
                  style="width:${(b.value / max) * 100}%;background:${b.color}"
                ></span>
              </span>
              <span class="hpct">${pct}%</span>
            </div>`
          })}
        </div>
      `
    }
    const sparse = !!this.axisLabels?.length
    const maxPct = Math.round((max / total) * 100)
    return html`
      <div class="heading">${this.heading}</div>
      <div class="plot">
        <div class="yaxis" title="Percent of total">
          <span>${maxPct}%</span>
          <span>0</span>
        </div>
        <div class="bars">
          ${this.bins.map(b => {
            const pct = Math.round((b.value / total) * 100)
            const title = b.hint ? `${pct}% ${b.hint}` : `${b.label}: ${pct}%`
            return html`
              <div class="col">
                <div class="track">
                  <div
                    class="bar"
                    style="height:${(b.value / max) * 100}%;background:${b.color}"
                    title=${title}
                  ></div>
                </div>
                ${sparse ? nothing : html`<span class="label" title=${b.label}>${b.label}</span>`}
              </div>
            `
          })}
        </div>
      </div>
      ${sparse
        ? html`<div class="axis">${this.axisLabels!.map(l => html`<span>${l}</span>`)}</div>`
        : nothing}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'distribution-chart': DistributionChart
  }
}
